import React, { useState } from 'react';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Dropdown,
  IconButton,
  ListDivider,
  Menu,
  MenuButton,
  MenuItem,
  Modal,
  ModalDialog,
} from '@mui/joy';
import { FaEllipsisV } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { IconWrapper } from '../../../components/IconWrapper';
import { arrangementDeleteBlock, type BillingArrangement } from '../arrangements.model';
import { arrangementsService } from '../arrangements.service';

/** Row menu for an arrangement on the Invoicing list: open, close/reopen and delete. */
export function ArrangementActionsMenu({
  arrangement,
  invoices,
  onUpdated,
  onDeleted,
}: {
  arrangement: BillingArrangement;
  invoices: Array<Record<string, any>>;
  onUpdated: (arrangement: BillingArrangement) => void;
  onDeleted: (id: string) => void;
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ title: string; text: string } | null>(null);
  const [confirming, setConfirming] = useState(false);

  const blocked = arrangementDeleteBlock(invoices);
  const drafts = invoices.length;

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
    } catch (err) {
      setMessage({ title: arrangement.lifecycleRef, text: err instanceof Error ? err.message : 'Something went wrong' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dropdown>
        <MenuButton
          slots={{ root: IconButton }}
          slotProps={{ root: { variant: 'plain', color: 'neutral', size: 'sm', disabled: busy, 'aria-label': `Actions for ${arrangement.lifecycleRef}` } as any }}
          onClick={(event) => event.stopPropagation()}
        >
          <IconWrapper icon={FaEllipsisV} />
        </MenuButton>
        <Menu placement="bottom-end" size="sm" onClick={(event) => event.stopPropagation()}>
          <MenuItem onClick={() => navigate(`/invoicing/${arrangement.lifecycleRef}`)}>Open</MenuItem>
          <MenuItem
            onClick={() => run(async () => {
              onUpdated(await arrangementsService.setStatus(arrangement.id, arrangement.status === 'open' ? 'closed' : 'open'));
            })}
          >
            {arrangement.status === 'open' ? 'Close' : 'Reopen'}
          </MenuItem>
          <ListDivider />
          <MenuItem
            color="danger"
            onClick={() => {
              if (blocked) {
                setMessage({ title: `${arrangement.lifecycleRef} cannot be deleted`, text: blocked });
                return;
              }
              setConfirming(true);
            }}
          >
            Delete…
          </MenuItem>
        </Menu>
      </Dropdown>

      <Modal open={confirming} onClose={() => !busy && setConfirming(false)}>
        <ModalDialog size="sm" role="alertdialog">
          <DialogTitle>Delete {arrangement.lifecycleRef}?</DialogTitle>
          <DialogContent>
            {arrangement.title}
            {drafts > 0 && ` and its ${drafts} draft invoice${drafts === 1 ? '' : 's'}`} will be deleted.
            The {arrangement.lifecycleRef} number is not reused.
          </DialogContent>
          <DialogActions>
            <Button
              color="danger"
              loading={busy}
              onClick={() => run(async () => {
                await arrangementsService.remove(arrangement.id);
                setConfirming(false);
                onDeleted(arrangement.id);
              })}
            >
              Delete
            </Button>
            <Button variant="plain" color="neutral" onClick={() => setConfirming(false)} disabled={busy}>Cancel</Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={!!message} onClose={() => setMessage(null)}>
        <ModalDialog size="sm">
          <DialogTitle>{message?.title}</DialogTitle>
          <DialogContent><Alert color="warning">{message?.text}</Alert></DialogContent>
          <DialogActions><Button onClick={() => setMessage(null)}>OK</Button></DialogActions>
        </ModalDialog>
      </Modal>
    </>
  );
}
